// Lead tracking (#783, #1054): the one place the app invokes `meta-capi` for
// Lead, and `ghl-lead-bridge`. The Purchase's checkout context goes through
// purchase-tracking.ts (#736).
//
// Every public lead surface (wizard + venue, /order, menu + package inquiry)
// used to carry its own copy of this block, and each copy needed its own
// consent guard (#741). Owning the invocation here means the gate is written
// once, and a new lead surface is consent-safe by calling this helper rather
// than by remembering to add a guard.

import { supabase } from "@/integrations/supabase/client";
import { trackLead, getFbp, getFbc } from "@/lib/analytics";
import { analyticsAllowed } from "@/lib/consent";
import { trackingHostAllowed } from "@/lib/tracking-host";
import { normalizeContentCategory } from "../../supabase/functions/meta-capi/lead-category.ts";

/**
 * Which table `meta-capi` and `ghl-lead-bridge` look the lead up in. Always
 * passed explicitly, so no caller relies on an edge function treating a missing
 * value as "event".
 */
type LeadSource = "event" | "quote" | "menu_inquiry";

interface LeadConversion {
  /** Row id `meta-capi` reads the lead's email/phone from. */
  rowId: string;
  source: LeadSource;
  /** Shared browser/server `event_id` Meta dedupes the pair on. Defaults to `rowId`. */
  dedupId?: string;
  /** Estimated or real conversion value in CAD (#278). Omitted -> a value-less Lead on both legs. */
  value?: number;
  /** The lead's funnel (#925), e.g. `wedding` or a menu slug. Normalized here, once,
   *  for both legs. Omitted or empty after normalizing -> no category on either leg. */
  contentCategory?: string;
}

/**
 * Fire a Lead conversion: the browser Pixel/GA4/Ads leg (`trackLead`) plus the
 * server-side Conversions API leg (`meta-capi`), sharing one `event_id` so Meta
 * dedupes the pair. `value` rides BOTH legs so whichever survives dedup carries
 * it for value-based bidding (#278). `fbp`/`fbc` ride the server leg so it stays
 * attributable to an ad click when the browser pixel is blocked (#505), which
 * is the case CAPI exists for.
 *
 * Gated on `analyticsAllowed()` (#486's opt-out cookie), covering both legs.
 * The browser leg can be stopped from the browser, and `trackLead` gates itself
 * too since #741. The CAPI leg is the reason this gate exists: it causes the
 * visitor's email to be looked up, hashed and sent to Meta server-side, and no
 * browser-side kill switch reaches it. A visitor who declined must not trigger
 * it at all.
 *
 * Also gated on a production host (#1303), so a lead submitted on a Vercel
 * preview sends Meta neither leg.
 *
 * Fire-and-forget and fully swallowed: analytics must NEVER reject a submit
 * mutation or surface a toast.
 */
export function trackLeadConversion({ rowId, source, dedupId = rowId, value, contentCategory }: LeadConversion): void {
  try {
    if (!analyticsAllowed() || !trackingHostAllowed()) return;
    const category = normalizeContentCategory(contentCategory);
    trackLead({ eventId: dedupId, value, currency: "CAD", contentCategory: category });
    supabase.functions
      .invoke("meta-capi", {
        body: {
          event_row_id: rowId,
          dedup_event_id: dedupId,
          lead_source: source,
          fbp: getFbp(),
          fbc: getFbc(),
          source_url: typeof window !== "undefined" ? window.location.href : null,
          ua: typeof navigator !== "undefined" ? navigator.userAgent : null,
          // Mirror the browser Lead's value so the deduped server event matches (#278).
          ...(value != null ? { value, currency: "CAD" } : {}),
          // Same normalized funnel tag as the browser Lead (#925).
          ...(category ? { content_category: category } : {}),
        },
      })
      .then(({ error }) => {
        if (error) console.warn("[meta-capi] invocation failed", error);
      })
      .catch((err) => console.warn("[meta-capi] threw", err));
  } catch (err) {
    console.warn("[analytics] lead tracking threw", err);
  }
}

/**
 * Hybrid GHL lead bridge (#205, epic #202): upsert the new lead's contact into
 * GHL so GHL can communicate with the prospect. Since #224 the bridge also owns
 * the confirmation email (instant automation step 0 via GHL, with a server-side
 * Resend fallback), so no client invokes send-event-confirmation itself. For a
 * `"quote"` source (a completed /order purchase) the bridge skips the nurture
 * sequence.
 *
 * Not consent-gated: GHL is the business's own CRM, not ad tracking, so a
 * visitor who declined analytics still gets bridged. `source` names the table
 * the bridge reads `id` from. The edge function is idempotent (guards on each
 * table's `ghl_synced_at`).
 *
 * Fire-and-forget and fully swallowed: GHL is a comms channel only and must
 * NEVER reject a submit mutation.
 */
export function bridgeLeadToGhl(source: LeadSource, id: string): void {
  try {
    supabase.functions
      .invoke("ghl-lead-bridge", { body: { source, id } })
      .then(({ error }) => {
        if (error) console.warn("[ghl-lead-bridge] invocation failed", error);
      })
      .catch((err) => console.warn("[ghl-lead-bridge] threw", err));
  } catch (err) {
    console.warn("[ghl-lead-bridge] threw", err);
  }
}
